/**
 * Dismissable notification banners: the flash messages, the site-shutdown and
 * COVID notices, the partner and staff management boxes, and the missing
 * invoicing details prompt. Each marks its outer element `data-alert` and puts
 * an `<a class="close" href="#">` inside it.
 *
 * Dismissal is a two-step the stylesheet finishes. Adding `alert-close` sets
 * `opacity: 0` against the `single-transition(opacity, 300ms, ease-out)` that
 * `alert-base()` gives every one of these containers, and the element is
 * removed once that transition ends — or once the fallback below expires,
 * whichever comes first.
 */

import { delegate } from 'common/delegate';

const ALERT_SELECTOR = '[data-alert]';
const CLOSE_SELECTOR = '[data-alert] .close';

/**
 * A floor under the `transitionend` wait, comfortably past the 300ms
 * `$alert-transition-speed` fade.
 *
 * `transitionend` only arrives if a transition actually runs, and whether one
 * does is not this module's to decide: a user stylesheet or accessibility
 * extension applying `transition: none !important`, or `alert-base()` losing
 * its `single-transition`, leaves the banner at `opacity: 0` and still in
 * flow — invisible, but holding its layout space for the life of the page.
 */
const REMOVE_FALLBACK_MS = 1000;

let initialized = false;

export default function initializeAlert(): void {
  if (initialized) return;
  initialized = true;

  delegate(document, 'click', CLOSE_SELECTOR, handleCloseClick);
}

function handleCloseClick(event: MouseEvent, close: Element): void {
  const banner = close.closest(ALERT_SELECTOR);
  if (!banner) return;

  // The close link is an `<a href="#">`, so leaving the default in place would
  // land a bare `#` in the address bar on every dismissal.
  event.preventDefault();

  // A banner already fading is already being removed by the wait the first
  // click set up. Repeated clicks would otherwise each add a listener and a
  // timer to the same element.
  if (banner.classList.contains('alert-close')) return;

  banner.classList.add('alert-close');

  // Aborting drops the `transitionend` wait; the fallback timer is left to
  // expire on its own, since by then `abort()` and `remove()` are both no-ops.
  const waiting = new AbortController();

  const removeBanner = (): void => {
    waiting.abort();
    banner.remove();
  };

  banner.addEventListener(
    'transitionend',
    transition => {
      // `transitionend` bubbles, and a banner can hold a field that
      // transitions: the venue staff box's internal-comment textarea fades its
      // border and background on focus and blur. Clicking the close link while
      // it has focus blurs it, and that transition ends partway through the
      // fade. Only the banner's own transition means the fade is done.
      if (transition.target !== banner) return;
      removeBanner();
    },
    { signal: waiting.signal }
  );

  window.setTimeout(removeBanner, REMOVE_FALLBACK_MS);
}
